import usePermitForm from "./usePermitForm";
import EnergyHarbourPermitFormState, {
  EnergyHarbourPermitFormState_default,
  EnergyHarbourPermitFormState_populateErrorMessages,
  EnergyHarbourPermitFormState_toRequest,
} from "../types/forms/Permits/EnergyHarbourPermit/EnergyHarbourPermitFormState";
import {
  CodeStatus,
  CounterSignatureLocation,
  CounterSignatureRequestStatus,
  HotWorkSection,
  PermitCategory,
  PermitType,
  SignatureType,
} from "../common/enums";
import {
  Date_dateOnly,
  Date_dateStringIsValid,
  Date_hasPassed,
  Date_hoursBetween,
  Date_isSameDate,
  Date_isSameDateAndTime,
  Date_isToday,
  Date_min,
  Date_toDateString,
  Date_toDateTimeString,
} from "../common/dateUtils";
import { useEffect, useRef, useState } from "react";
import { Transformation } from "../types/functions";
import { Array_sum, Array_unorderedEquals } from "../common/arrayUtils";
import HotWorkConditionsFormState, {
  HotWorkFormState_defaultHotWorkCatOneConditions,
  HotWorkFormState_defaultHotWorkCatThreeConditions,
  HotWorkFormState_defaultHotWorkCatTwoConditions,
  HotWorkFormState_toRequest,
} from "../types/forms/Permits/EnergyHarbourPermit/HotWorkFormState";
import HotWorkCategoryOneOptionsFormState from "../types/forms/Permits/EnergyHarbourPermit/HotWorkCategoryOneOptionsFormState";
import HotWorkCategoryTwoOptionsFormState from "../types/forms/Permits/EnergyHarbourPermit/HotWorkCategoryTwoOptionsFormState";
import ProtectiveEquipmentFormState from "../types/forms/Permits/Conditions/ProtectiveEquipmentFormState";
import MeasuresFormState from "../types/forms/Permits/Conditions/MeasuresFormState";
import RiskIdentificationsFormState from "../types/forms/Permits/Conditions/RiskIdentificationsFormState";
import PermitAgreementFormState from "../types/forms/Permits/EnergyHarbourPermit/PermitAgreementFormState";
import EnergyHarbourPermit, {
  EnergyHarbourPermit_toFormState,
} from "../types/permits/EnergyHarbourPermit";
import { Signature_empty } from "../types/Signature";
import useSignature from "./useSignature";
import useRiskIdentifications from "./useRiskIdentificationsUIState";
import useMeasures from "./useMeasuesState";
import CounterSignatureRequestRecipient from "../types/CounterSignatureRequestRecipient";
import {
  checkoutPermitAsync,
  finishPermitAsync,
  getDepartmentById,
  getEnergyHarbourCustomersAsync,
  getPermitDocumentById,
  getPermitDocumentBySequenceIdentifier,
  getPermitDocumentByWorKPermitId,
  updateEnergyHarbourHotWorkAsync,
  updateEnergyHarbourPermitParticipantsAsync,
  updatePermitAgreementEndDateAsync,
  updatePositionsAsync,
} from "../apiClient";
import {
  showDangerDialog,
  showDialogAsync,
  showSuccessAlert,
  showValidationErrorDialog,
} from "../common";
import CounterSignatureRequest, {
  CounterSignatureRequest_empty,
} from "../types/CounterSignatureRequest";
import useCounterSignatureRequestInput, {
  CounterSignatureRequestInputManager,
} from "./useCounterSignatureRequestInput";
import { EnergyHarbourPermitFormProps } from "../components/Forms/Permits/EnergyHarbourPermitForm";
import DivingPermit, {
  DivingPermit_toEnergyHarbourPermitFormState,
} from "../types/permits/DivingPermit";
import UnderWaterWorkPermit, {
  UnderWaterWorkPermit_toEnergyHarbourPermitFormState,
} from "../types/permits/UnderWaterWorkPermit";
import EnergyHarbourPermitRequest from "../types/requests/EnergyHarbourPermitRequest";
import {
  PositionPickerRow_clearErrors,
  PositionPickerRow_toPosition,
} from "../types/forms/Permits/PositionPickerRow";
import UpdateEnergyHarbourPermitParticipantsRequest from "../types/requests/UpdateEnergyHarbourPermitParticipantsRequest";
import { PersonFormElement_toPerson } from "../types/forms/PersonFormElement";
import useDateInput from "./useDateInput";
import HotWorkCategoryThreeOptionsFormState from "../types/forms/Permits/EnergyHarbourPermit/HotWorkCategoryThreeOptionsFormState";
import { hasHotWorkSignatureClearingChanges } from "../common/formStateUtils";
import positionUtils from "../common/positionUtils";
import { HotWork_toFormState } from "../types/permits/EnergyHarbourPermit/HotWorkConditions";
import { EnergyHarbourCustomer } from "../types/EnergyHarbourCustomer";

function useEnergyHarbourPermitForm(props: EnergyHarbourPermitFormProps) {
  const [
    showHotWorkCategoryOneOptionsModal,
    setShowHotWorkCategoryOneOptionsModal,
  ] = useState(false);
  const [
    showHotWorkCategoryTwoOptionsModal,
    setShowHotWorkCategoryTwoOptionsModal,
  ] = useState(false);
  const [
    showHotWorkCategoryThreeOptionsModal,
    setShowHotWorkCategoryThreeOptionsModal,
  ] = useState(false);

  const [energyHarbourCustomers, setEnergyHarbourCustomers] = useState<
    EnergyHarbourCustomer[]
  >([]);

  const permitAgreementStartDateRef = useRef<HTMLInputElement>(null);
  const permitAgreementEndDateRef = useRef<HTMLInputElement>(null);

  const baseFormManager = usePermitForm({
    ...props,
    defaultFormState: EnergyHarbourPermitFormState_default,
    permitType: PermitType.EnergyHarbourPermit,
    toFormState: EnergyHarbourPermit_toFormState,
    toRequest: toRequest,
    populateErrorMessages: EnergyHarbourPermitFormState_populateErrorMessages,
    fetchDataAsync: fetchDataAsync,
    clearErrorMessages: (formState) =>
      EnergyHarbourPermitFormState_populateErrorMessages(formState, {}),
    updateAsync: updatePermitHandlerAsync,
  });

  const startDateRef = useRef<HTMLInputElement>(null);
  const endDateRef = useRef<HTMLInputElement>(null);

  const startDateHandler = useDateInput({
    date: baseFormManager.formState.startDate.value,
    ref: startDateRef,
    comparer: Date_isSameDate,
    toStringValue: Date_toDateString,
    onDateChange: startDateChangeHandler,
  });

  const endDateHandler = useDateInput({
    date: baseFormManager.formState.endDate.value,
    ref: endDateRef,
    comparer: Date_isSameDate,
    toStringValue: Date_toDateString,
    onDateChange: endDateChangeHandler,
  });

  function startDateChangeHandler(updatedStartDate: Date) {
    const updatedEndDate =
      !Date_isSameDate(
        baseFormManager.formState.endDate.value,
        updatedStartDate,
      ) && baseFormManager.formState.endDate.value < updatedStartDate
        ? new Date(updatedStartDate)
        : new Date(baseFormManager.formState.endDate.value);

    baseFormManager.registerFormStateChange((prevValue) => ({
      ...prevValue,
      startDate: { ...prevValue.startDate, value: updatedStartDate },
      endDate: { ...prevValue.endDate, value: updatedEndDate },
    }));
  }

  function endDateChangeHandler(updatedEndDate: Date) {
    const updatedStartDate =
      !Date_isSameDate(
        baseFormManager.formState.startDate.value,
        updatedEndDate,
      ) && baseFormManager.formState.startDate.value > updatedEndDate
        ? new Date(updatedEndDate)
        : new Date(baseFormManager.formState.startDate.value);

    baseFormManager.registerFormStateChange((prevValue) => ({
      ...prevValue,
      startDate: { ...prevValue.startDate, value: updatedStartDate },
      endDate: { ...prevValue.endDate, value: updatedEndDate },
    }));
  }

  const permitAgreementStartDateHandler = useDateInput({
    comparer: Date_isSameDateAndTime,
    date: baseFormManager.formState.permitAgreement.startDate.value,
    ref: permitAgreementStartDateRef,
    toStringValue: Date_toDateTimeString,
    onDateChange: permitAgreementStartDateChangeHandler,
  });

  const permitAgreementEndDateHandler = useDateInput({
    comparer: Date_isSameDateAndTime,
    date: baseFormManager.formState.permitAgreement.endDate.value,
    ref: permitAgreementEndDateRef,
    toStringValue: Date_toDateTimeString,
    onDateChange: permitAgreementEndDateChangeHandler,
  });

  function permitAgreementStartDateChangeHandler(
    updatedPermitAgreementStartDate: Date,
  ) {
    baseFormManager.registerFormStateChange((prevValue) => {
      return {
        ...prevValue,
        permitAgreement: {
          ...prevValue.permitAgreement,
          startDate: {
            ...prevValue.permitAgreement.startDate,
            value: updatedPermitAgreementStartDate,
          },
        },
      };
    });
  }

  function permitAgreementEndDateChangeHandler(
    updatedPermitAgreementEndDate: Date,
  ) {
    baseFormManager.registerFormStateChange((prevValue) => {
      return {
        ...prevValue,
        permitAgreement: {
          ...prevValue.permitAgreement,
          endDate: {
            ...prevValue.permitAgreement.endDate,
            value: updatedPermitAgreementEndDate,
          },
        },
      };
    });
  }

  const riskIdentificationsUIState = useRiskIdentifications();
  const measuresUIState = useMeasures();

  const {
    storedPermit,
    formState,
    cancelCounterSignatureRequestHandlerAsync,
    sendCounterSignatureRequestHandlerAsync,
  } = baseFormManager;

  useEffect(() => {
    baseFormManager.setFormState((prevValue) => ({
      ...prevValue,
      positions: prevValue.positions.map((pos) => ({
        ...pos,
        category:
          !pos.category ||
          prevValue.permitCategories.value.includes(pos.category)
            ? pos.category
            : undefined,
      })),
    }));
  }, [formState.permitCategories.value]);

  const powerOffResponsibleElectricianSignature = storedPermit
    ? storedPermit.measures.electricFenceChecklist
        .powerOffResponsibleElectricianSignature
    : Signature_empty(SignatureType.PowerOffResponsibleElectrician);

  const powerOffWorkLeaderSignature = storedPermit
    ? storedPermit.measures.electricFenceChecklist.powerOffWorkLeaderSignature
    : Signature_empty(SignatureType.PowerOffWorkLeader);

  const powerOffDirectorFlammableProductSignature = storedPermit
    ? storedPermit.measures.electricFenceChecklist
        .powerOffDirectorFlammableProductSignature
    : Signature_empty(SignatureType.PowerOffDirectorFlammableProduct);

  const powerOffExternalDirectorFlammableProductSignature = storedPermit
    ? storedPermit.measures.electricFenceChecklist
        .powerOffExternalDirectorFlammableProductSignature
    : Signature_empty(SignatureType.PowerOffExternalDirectorFlammableProduct);

  const powerOnResponsibleElectricianSignature = storedPermit
    ? storedPermit.measures.electricFenceChecklist
        .powerOnResponsibleElectricianSignature
    : Signature_empty(SignatureType.PowerOnResponsibleElectrician);

  const powerOnWorkLeaderSignature = storedPermit
    ? storedPermit.measures.electricFenceChecklist.powerOnWorkLeaderSignature
    : Signature_empty(SignatureType.PowerOnWorkLeader);

  const powerOnDirectorFlammableProductSignature = storedPermit
    ? storedPermit.measures.electricFenceChecklist
        .powerOnDirectorFlammableProductSignature
    : Signature_empty(SignatureType.PowerOnDirectorFlammableProduct);

  const powerOnExternalDirectorFlammableProductSignature = storedPermit
    ? storedPermit.measures.electricFenceChecklist
        .powerOnExternalDirectorFlammableProductSignature
    : Signature_empty(SignatureType.PowerOnExternalDirectorFlammableProduct);

  const craneOperatorSignature = storedPermit
    ? storedPermit.riskIdentifications.heavyLiftingChecklist
        .craneOperatorSignature
    : Signature_empty(SignatureType.CraneOperator);

  const safetyAgreementPermitSeekerSignature = storedPermit
    ? storedPermit.safetyAgreement.safetyAgreementPermitSeekerSignature
    : Signature_empty(SignatureType.SafetyAgreementPermitSeeker);

  const permitAgreementProviderSignature = storedPermit
    ? storedPermit.permitAgreement.permitAgreementProviderSignature
    : Signature_empty(SignatureType.PermitAgreementPermitProvider);

  const permitAgreementSeekerSignature = storedPermit
    ? storedPermit.permitAgreement.permitAgreementSeekerSignature
    : Signature_empty(SignatureType.PermitAgreementPermitSeeker);

  const isFinishedPermitProviderSignature = storedPermit
    ? storedPermit.isFinishedPermitProviderSignature
    : Signature_empty(SignatureType.IsFinishedPermitProvider);

  const isFinishedPermitSeekerSignature = storedPermit
    ? storedPermit.isFinishedPermitSeekerSignature
    : Signature_empty(SignatureType.IsFinishedPermitSeeker);

  const safeHarbourEducationControlledSignature = storedPermit
    ? storedPermit.safeHarbourEducationControlledSignature
    : Signature_empty(SignatureType.SafeHarbourEducationControlled);

  const hotWorkInternalDirectorFlammableProductSignature = storedPermit
    ? storedPermit.hotWorkInternalDirectorFlammableProductSignature
    : Signature_empty(SignatureType.HotWorkInternalDirectorFlammableProduct);

  const hotWorkExternalDirectorFlammableProductSignature = storedPermit
    ? storedPermit.hotWorkExternalDirectorFlammableProductSignature
    : Signature_empty(SignatureType.HotWorkExternalDirectorFlammableProduct);

  const powerOffPortOfficerSignature = storedPermit
    ? storedPermit.measures.electricFenceChecklist.powerOffPortOfficerSignature
    : Signature_empty(SignatureType.PowerOffPortOfficer);

  const powerOnPortOfficerSignature = storedPermit
    ? storedPermit.measures.electricFenceChecklist.powerOnPortOfficerSignature
    : Signature_empty(SignatureType.PowerOnPortOfficer);

  const powerOffResponsibleElectricianSignatureManager = useSignature({
    signature: powerOffResponsibleElectricianSignature,
    onSaveSignature: baseFormManager.createSignatureManagerAsync,
    onRemoveSignature: baseFormManager.removeSignatureManagerAsync,
    resources: props.resources,
    baseUserInfo: props.baseUserInfo,
    showSignatureModalEventListener: () =>
      measuresUIState.setShowElectricFenceChecklistModal(false),
    hideSignatureModalEventListener: () =>
      measuresUIState.setShowElectricFenceChecklistModal(true),
    hideSignature: !!baseFormManager.isNewPermit,
  });

  const powerOffWorkLeaderSignatureManager = useSignature({
    signature: powerOffWorkLeaderSignature,
    onSaveSignature: baseFormManager.createSignatureManagerAsync,
    onRemoveSignature: baseFormManager.removeSignatureManagerAsync,
    resources: props.resources,
    baseUserInfo: props.baseUserInfo,
    showSignatureModalEventListener: () =>
      measuresUIState.setShowElectricFenceChecklistModal(false),
    hideSignatureModalEventListener: () =>
      measuresUIState.setShowElectricFenceChecklistModal(true),
    hideSignature: !!baseFormManager.isNewPermit,
  });

  const powerOffDirectorFlammableProductSignatureManager = useSignature({
    signature: powerOffDirectorFlammableProductSignature,
    onSaveSignature: baseFormManager.createSignatureManagerAsync,
    onRemoveSignature: baseFormManager.removeSignatureManagerAsync,
    resources: props.resources,
    baseUserInfo: props.baseUserInfo,
    showSignatureModalEventListener: () =>
      measuresUIState.setShowElectricFenceChecklistModal(false),
    hideSignatureModalEventListener: () =>
      measuresUIState.setShowElectricFenceChecklistModal(true),
    hideSignature: !!baseFormManager.isNewPermit,
  });

  const powerOffPortOfficerSignatureManager = useSignature({
    signature: powerOffPortOfficerSignature,
    onSaveSignature: baseFormManager.createSignatureManagerAsync,
    onRemoveSignature: baseFormManager.removeSignatureManagerAsync,
    resources: props.resources,
    baseUserInfo: props.baseUserInfo,
    showSignatureModalEventListener: () =>
      measuresUIState.setShowElectricFenceChecklistModal(false),
    hideSignatureModalEventListener: () =>
      measuresUIState.setShowElectricFenceChecklistModal(true),
    hideSignature: !!baseFormManager.isNewPermit,
  });

  const powerOnPortOfficerSignatureManager = useSignature({
    signature: powerOnPortOfficerSignature,
    onSaveSignature: baseFormManager.createSignatureManagerAsync,
    onRemoveSignature: baseFormManager.removeSignatureManagerAsync,
    resources: props.resources,
    baseUserInfo: props.baseUserInfo,
    showSignatureModalEventListener: () =>
      measuresUIState.setShowElectricFenceChecklistModal(false),
    hideSignatureModalEventListener: () =>
      measuresUIState.setShowElectricFenceChecklistModal(true),
    hideSignature: !!baseFormManager.isNewPermit,
  });

  const powerOffExternalDirectorFlammableProductSignatureManager = useSignature(
    {
      signature: powerOffExternalDirectorFlammableProductSignature,
      onSaveSignature: baseFormManager.createSignatureManagerAsync,
      onRemoveSignature: baseFormManager.removeSignatureManagerAsync,
      resources: props.resources,
      baseUserInfo: props.baseUserInfo,
      showSignatureModalEventListener: () =>
        measuresUIState.setShowElectricFenceChecklistModal(false),
      hideSignatureModalEventListener: () =>
        measuresUIState.setShowElectricFenceChecklistModal(true),
      hideSignature: !!baseFormManager.isNewPermit,
    },
  );

  const powerOnResponsibleElectricianSignatureManager = useSignature({
    signature: powerOnResponsibleElectricianSignature,
    onSaveSignature: baseFormManager.createSignatureManagerAsync,
    onRemoveSignature: baseFormManager.removeSignatureManagerAsync,
    resources: props.resources,
    baseUserInfo: props.baseUserInfo,
    showSignatureModalEventListener: () =>
      measuresUIState.setShowElectricFenceChecklistModal(false),
    hideSignatureModalEventListener: () =>
      measuresUIState.setShowElectricFenceChecklistModal(true),
    hideSignature: !!baseFormManager.isNewPermit,
  });

  const powerOnWorkLeaderSignatureManager = useSignature({
    signature: powerOnWorkLeaderSignature,
    onSaveSignature: baseFormManager.createSignatureManagerAsync,
    onRemoveSignature: baseFormManager.removeSignatureManagerAsync,
    resources: props.resources,
    baseUserInfo: props.baseUserInfo,
    showSignatureModalEventListener: () =>
      measuresUIState.setShowElectricFenceChecklistModal(false),
    hideSignatureModalEventListener: () =>
      measuresUIState.setShowElectricFenceChecklistModal(true),
    hideSignature: !!baseFormManager.isNewPermit,
  });

  const powerOnDirectorFlammableProductSignatureManager = useSignature({
    signature: powerOnDirectorFlammableProductSignature,
    onSaveSignature: baseFormManager.createSignatureManagerAsync,
    onRemoveSignature: baseFormManager.removeSignatureManagerAsync,
    resources: props.resources,
    baseUserInfo: props.baseUserInfo,
    showSignatureModalEventListener: () =>
      measuresUIState.setShowElectricFenceChecklistModal(false),
    hideSignatureModalEventListener: () =>
      measuresUIState.setShowElectricFenceChecklistModal(true),
    hideSignature: !!baseFormManager.isNewPermit,
  });

  const powerOnExternalDirectorFlammableProductSignatureManager = useSignature({
    signature: powerOnExternalDirectorFlammableProductSignature,
    onSaveSignature: baseFormManager.createSignatureManagerAsync,
    onRemoveSignature: baseFormManager.removeSignatureManagerAsync,
    resources: props.resources,
    baseUserInfo: props.baseUserInfo,
    showSignatureModalEventListener: () =>
      measuresUIState.setShowElectricFenceChecklistModal(false),
    hideSignatureModalEventListener: () =>
      measuresUIState.setShowElectricFenceChecklistModal(true),
    hideSignature: !!baseFormManager.isNewPermit,
  });

  const craneOperatorSignatureManager = useSignature({
    signature: craneOperatorSignature,
    onSaveSignature: baseFormManager.createSignatureManagerAsync,
    onRemoveSignature: baseFormManager.removeSignatureManagerAsync,
    resources: props.resources,
    baseUserInfo: props.baseUserInfo,
    showSignatureModalEventListener: () =>
      riskIdentificationsUIState.setShowHeavyLiftingChecklistModal(false),
    hideSignatureModalEventListener: () =>
      riskIdentificationsUIState.setShowHeavyLiftingChecklistModal(true),
    hideSignature: !!baseFormManager.isNewPermit,
    includePhoneNumber: true,
  });

  const hotWorkInternalDirectorFlammableProductSignatureManager = useSignature({
    signature: hotWorkInternalDirectorFlammableProductSignature,
    onSaveSignature: baseFormManager.createSignatureManagerAsync,
    onRemoveSignature: baseFormManager.removeSignatureManagerAsync,
    resources: props.resources,
    baseUserInfo: props.baseUserInfo,
    hideSignature: !!baseFormManager.isNewPermit,
    disableSignButton: baseFormManager.hasUnsavedChanges,
    signButtonTooltip: baseFormManager.hasUnsavedChanges
      ? props.resources.get("SaveChangesBeforeSigning")
      : "",
  });

  const hotWorkExternalDirectorFlammableProductSignatureManager = useSignature({
    signature: hotWorkExternalDirectorFlammableProductSignature,
    onSaveSignature: baseFormManager.createSignatureManagerAsync,
    onRemoveSignature: baseFormManager.removeSignatureManagerAsync,
    resources: props.resources,
    baseUserInfo: props.baseUserInfo,
    hideSignature: !!baseFormManager.isNewPermit,
    disableSignButton: baseFormManager.hasUnsavedChanges,
    signButtonTooltip: baseFormManager.hasUnsavedChanges
      ? props.resources.get("SaveChangesBeforeSigning")
      : "",
  });

  const safetyAgreementPermitSeekerSignatureManager = useSignature({
    signature: safetyAgreementPermitSeekerSignature,
    onSaveSignature: baseFormManager.createSignatureManagerAsync,
    onRemoveSignature: baseFormManager.removeSignatureManagerAsync,
    resources: props.resources,
    baseUserInfo: props.baseUserInfo,
  });

  const permitAgreementPermitProviderSignatureManager = useSignature({
    signature: permitAgreementProviderSignature,
    onSaveSignature: baseFormManager.createSignatureManagerAsync,
    onRemoveSignature: baseFormManager.removeSignatureManagerAsync,
    resources: props.resources,
    baseUserInfo: props.baseUserInfo,
  });

  const permitAgreementPermitSeekerSignatureManager = useSignature({
    signature: permitAgreementSeekerSignature,
    onSaveSignature: baseFormManager.createSignatureManagerAsync,
    onRemoveSignature: baseFormManager.removeSignatureManagerAsync,
    resources: props.resources,
    baseUserInfo: props.baseUserInfo,
  });

  const isFinishedPermitProviderSignatureManager = useSignature({
    signature: isFinishedPermitProviderSignature,
    onSaveSignature: saveIsFinishedPermitProviderSignatureManagerAsync,
    onRemoveSignature: baseFormManager.removeSignatureManagerAsync,
    resources: props.resources,
    baseUserInfo: props.baseUserInfo,
  });

  const isFinishedPermitSeekerSignatureManager = useSignature({
    signature: isFinishedPermitSeekerSignature,
    onSaveSignature: saveIsFinishedPermitSeekerSignatureManagerAsync,
    onRemoveSignature: baseFormManager.removeSignatureManagerAsync,
    resources: props.resources,
    baseUserInfo: props.baseUserInfo,
  });

  const safeHarbourEducationControlledSignatureManager = useSignature({
    signature: safeHarbourEducationControlledSignature,
    onSaveSignature: baseFormManager.createSignatureManagerAsync,
    onRemoveSignature: baseFormManager.removeSignatureManagerAsync,
    resources: props.resources,
    baseUserInfo: props.baseUserInfo,
    hideSignature: !!baseFormManager.isNewPermit,
  });

  const hotWorkCounterSignatureRequest: CounterSignatureRequest = storedPermit
    ? storedPermit.hotWork.counterSignatureRequest
    : CounterSignatureRequest_empty(CounterSignatureLocation.HotWork);

  const hotWorkCounterSignatureRequestHandler: CounterSignatureRequestInputManager =
    useCounterSignatureRequestInput({
      counterSignatureRequest: hotWorkCounterSignatureRequest,
      onCancelCounterSignatureRequestAsync: async () =>
        await cancelCounterSignatureRequestHandlerAsync(
          hotWorkCounterSignatureRequest,
        ),
      onSendCounterSignatureRequestAsync: async (
        recipient: CounterSignatureRequestRecipient,
      ) =>
        await sendCounterSignatureRequestHandlerAsync(
          recipient,
          CounterSignatureLocation.HotWork,
        ),
      resources: props.resources,
      showCounterSignatureRequestSection: !baseFormManager.isNewPermit,
      hasUnsavedChanges: baseFormManager.hasUnsavedChanges,
    });

  const powerOffCounterSignatureRequest: CounterSignatureRequest = storedPermit
    ? storedPermit.measures.electricFenceChecklist
        .powerOffCounterSignatureRequest
    : CounterSignatureRequest_empty(
        CounterSignatureLocation.ElectricFencePowerOff,
      );

  const electricFencePowerOffCounterSignatureRequestHandler: CounterSignatureRequestInputManager =
    useCounterSignatureRequestInput({
      counterSignatureRequest: powerOffCounterSignatureRequest,
      onCancelCounterSignatureRequestAsync: async () =>
        cancelCounterSignatureRequestHandlerAsync(
          powerOffCounterSignatureRequest,
        ),
      onSendCounterSignatureRequestAsync: async (
        recipient: CounterSignatureRequestRecipient,
      ) =>
        sendCounterSignatureRequestHandlerAsync(
          recipient,
          CounterSignatureLocation.ElectricFencePowerOff,
        ),
      resources: props.resources,
      showCounterSignatureRequestSection: !baseFormManager.isNewPermit,
      hasUnsavedChanges: baseFormManager.hasUnsavedChanges,
    });

  const powerOnCounterSignatureRequest: CounterSignatureRequest = storedPermit
    ? storedPermit.measures.electricFenceChecklist
        .powerOnCounterSignatureRequest
    : CounterSignatureRequest_empty(
        CounterSignatureLocation.ElectricFencePowerOn,
      );

  const electricFencePowerOnCounterSignatureRequestHandler: CounterSignatureRequestInputManager =
    useCounterSignatureRequestInput({
      counterSignatureRequest: powerOnCounterSignatureRequest,
      onCancelCounterSignatureRequestAsync: async () =>
        cancelCounterSignatureRequestHandlerAsync(
          powerOnCounterSignatureRequest,
        ),
      onSendCounterSignatureRequestAsync: async (
        recipient: CounterSignatureRequestRecipient,
      ) =>
        sendCounterSignatureRequestHandlerAsync(
          recipient,
          CounterSignatureLocation.ElectricFencePowerOn,
        ),
      resources: props.resources,
      showCounterSignatureRequestSection: !baseFormManager.isNewPermit,
      hasUnsavedChanges: baseFormManager.hasUnsavedChanges,
    });

  function toRequest(
    energyHarbourPermitFormState: EnergyHarbourPermitFormState,
  ): EnergyHarbourPermitRequest {
    const request: EnergyHarbourPermitRequest =
      EnergyHarbourPermitFormState_toRequest(energyHarbourPermitFormState);
    request.riskIdentifications.divingChecklist.fromDivingPermitDocumentId =
      props.divingPermitDocumentId;
    request.riskIdentifications.raftChecklist.fromUnderWaterWorkPermitDocumentId =
      props.underWaterWorkPermitDocumentId;

    return request;
  }

  async function fetchDataAsync() {
    const promises: Promise<any>[] = [
      getDepartmentById(props.departmentId),
      getEnergyHarbourCustomersAsync(),
    ];

    if (props.documentReferenceId) {
      promises.push(
        getPermitDocumentById<EnergyHarbourPermit>(
          props.documentReferenceId,
          PermitType.EnergyHarbourPermit,
        ),
      );
    } else if (props.sequenceIdentifier) {
      promises.push(
        getPermitDocumentBySequenceIdentifier<EnergyHarbourPermit>(
          props.sequenceIdentifier,
          PermitType.EnergyHarbourPermit,
        ),
      );
    } else if (props.workPermitId) {
      promises.push(
        getPermitDocumentByWorKPermitId<EnergyHarbourPermit>(
          props.workPermitId,
          PermitType.EnergyHarbourPermit,
        ),
      );
    } else if (props.copyOfDocumentReferenceId) {
      promises.push(
        getPermitDocumentById<EnergyHarbourPermit>(
          props.copyOfDocumentReferenceId,
          PermitType.EnergyHarbourPermit,
        ),
      );
    } else if (props.divingPermitDocumentId) {
      promises.push(
        getPermitDocumentById<DivingPermit>(
          props.divingPermitDocumentId,
          PermitType.DivingPermit,
        ),
      );
    } else if (props.underWaterWorkPermitDocumentId) {
      promises.push(
        getPermitDocumentById<UnderWaterWorkPermit>(
          props.underWaterWorkPermitDocumentId,
          PermitType.UnderWaterWorkPermit,
        ),
      );
    }

    const [
      getDepartmentResult,
      getEnergyHarbourCustomersResult,
      getPermitResult,
    ] = await Promise.all(promises);

    if (getDepartmentResult.failed) {
      console.error(getDepartmentResult.error);
      showDangerDialog(
        props.resources.get("SomethingWentWrong"),
        props.resources.get("GetDepartmentError"),
      );
      return;
    }

    if (getEnergyHarbourCustomersResult.failed) {
      console.error(getEnergyHarbourCustomersResult.error);
      showDangerDialog(
        props.resources.get("SomethingWentWrong"),
        props.resources.get("RefreshPageAndTryAgain"),
      );
      return;
    }

    if (getPermitResult && getPermitResult.failed) {
      console.error(getPermitResult.error);
      showDangerDialog(
        props.resources.get("SomethingWentWrong"),
        props.resources.get("GetPermitError"),
      );
      return;
    }

    const hasStoredPermit =
      getPermitResult &&
      (props.documentReferenceId ||
        props.sequenceIdentifier ||
        props.workPermitId);
    if (hasStoredPermit) {
      baseFormManager.setStoredPermit(getPermitResult.value);
      baseFormManager.setFormState(
        EnergyHarbourPermit_toFormState(getPermitResult.value),
      );
    } else if (getPermitResult && props.copyOfDocumentReferenceId) {
      //
      // getPermitResult.value is the permit with id copyOfDocumentReferenceId. I.e. the permit that is copied from.
      //
      const copiedFormState = EnergyHarbourPermit_toFormState(
        getPermitResult.value,
      );

      const copiedPermit = getPermitResult.value as EnergyHarbourPermit;
      if (
        !copiedPermit.supportsCategorySpecificHotWorkConditions &&
        copiedPermit.permitCategories.length > 0
      ) {
        //
        // If the source permit does not support category specific hot work conditions (in which case it will also only have a single category)
        // then the values from the single hot work condition section should be copied to the category specific section of the new permit.
        // Otherwise, the category specific sections will be empty.
        //

        switch (copiedPermit.permitCategories[0]) {
          case PermitCategory.HotWorkCategoryOne:
            copiedFormState.hotWorkCategoryOneConditions = HotWork_toFormState(
              copiedPermit.hotWork,
            );
            copiedFormState.hotWorkCategoryTwoConditions =
              HotWorkFormState_defaultHotWorkCatTwoConditions();
            copiedFormState.hotWorkCategoryThreeConditions =
              HotWorkFormState_defaultHotWorkCatThreeConditions();
            break;
          case PermitCategory.HotWorkCategoryTwo:
            copiedFormState.hotWorkCategoryOneConditions =
              HotWorkFormState_defaultHotWorkCatOneConditions();
            copiedFormState.hotWorkCategoryTwoConditions = HotWork_toFormState(
              copiedPermit.hotWork,
            );
            copiedFormState.hotWorkCategoryThreeConditions =
              HotWorkFormState_defaultHotWorkCatThreeConditions();
            break;
          case PermitCategory.HotWorkCategoryThree:
            copiedFormState.hotWorkCategoryOneConditions =
              HotWorkFormState_defaultHotWorkCatOneConditions();
            copiedFormState.hotWorkCategoryTwoConditions =
              HotWorkFormState_defaultHotWorkCatTwoConditions();
            copiedFormState.hotWorkCategoryThreeConditions =
              HotWork_toFormState(copiedPermit.hotWork);
            break;
        }
      }

      for (const positionRow of copiedFormState.positions) {
        positionUtils.addPositionOffset(positionRow);
      }

      baseFormManager.setFormState(copiedFormState);
    } else if (getPermitResult && props.divingPermitDocumentId) {
      //
      // getPermitResult.value is the permit with id divingPermitDocumentId. I.e. the permit that prefills the form.
      //
      baseFormManager.setFormState(
        DivingPermit_toEnergyHarbourPermitFormState(getPermitResult.value),
      );
    } else if (getPermitResult && props.underWaterWorkPermitDocumentId) {
      //
      // getPermitResult.value is the permit with id underWaterWorkPermitId. I.e. the permit that prefills the form.
      //
      baseFormManager.setFormState(
        UnderWaterWorkPermit_toEnergyHarbourPermitFormState(
          getPermitResult.value,
        ),
      );
    }

    setEnergyHarbourCustomers(getEnergyHarbourCustomersResult.value);
    baseFormManager.setDepartment(getDepartmentResult.value);
  }

  function permitCategoryChangeHandler(clickedCategory: PermitCategory) {
    if (!formState.permitCategories.value.includes(clickedCategory)) {
      permitCategoryAddedHandler(clickedCategory);
      return;
    }

    baseFormManager.registerFormStateChange((prevValue) => ({
      ...prevValue,
      permitCategories: {
        ...prevValue.permitCategories,
        value: prevValue.permitCategories.value.filter(
          (category) => category !== clickedCategory,
        ),
      },
    }));
  }

  function permitCategoryAddedHandler(newPermitCategory: PermitCategory) {
    if (newPermitCategory === PermitCategory.HotWorkCategoryOne) {
      setShowHotWorkCategoryOneOptionsModal(true);
    } else if (newPermitCategory === PermitCategory.HotWorkCategoryTwo) {
      setShowHotWorkCategoryTwoOptionsModal(true);
    } else if (newPermitCategory === PermitCategory.HotWorkCategoryThree) {
      setShowHotWorkCategoryThreeOptionsModal(true);
    }

    baseFormManager.registerFormStateChange((prevValue) => ({
      ...prevValue,
      permitCategories: {
        ...prevValue.permitCategories,
        value:
          !storedPermit || storedPermit.supportsMultiplePermitCategories
            ? [...prevValue.permitCategories.value, newPermitCategory]
            : [newPermitCategory],
      },
    }));
  }

  function permitCategoryRemovedHandler(removedPermitCategory: PermitCategory) {
    baseFormManager.registerFormStateChange((prevValue) => ({
      ...prevValue,
      permitCategories: {
        ...prevValue.permitCategories,
        value: prevValue.permitCategories.value.filter(
          (category) => category !== removedPermitCategory,
        ),
      },
    }));
  }

  function hotWorkCategoryOneOptionsChangeHandler(
    hotWorkCategoryOneOptionsTransformation: Transformation<HotWorkCategoryOneOptionsFormState>,
  ) {
    baseFormManager.registerFormStateChange((prevValue) => ({
      ...prevValue,
      hotWorkCategoryOneOptions: hotWorkCategoryOneOptionsTransformation(
        prevValue.hotWorkCategoryOneOptions,
      ),
    }));
  }

  function hotWorkCategoryTwoOptionsChangeHandler(
    hotWorkCategoryTwoOptionsTransformation: Transformation<HotWorkCategoryTwoOptionsFormState>,
  ) {
    baseFormManager.registerFormStateChange((prevValue) => ({
      ...prevValue,
      hotWorkCategoryTwoOptions: hotWorkCategoryTwoOptionsTransformation(
        prevValue.hotWorkCategoryTwoOptions,
      ),
    }));
  }

  function hotWorkCategoryThreeOptionsChangeHandler(
    hotWorkCategoryThreeOptionsTransformation: Transformation<HotWorkCategoryThreeOptionsFormState>,
  ) {
    baseFormManager.registerFormStateChange((prevValue) => ({
      ...prevValue,
      hotWorkCategoryThreeOptions: hotWorkCategoryThreeOptionsTransformation(
        prevValue.hotWorkCategoryThreeOptions,
      ),
    }));
  }

  function hotWorkChangeHandler(
    hotWorkTransformation: Transformation<HotWorkConditionsFormState>,
    hotWorkSection: HotWorkSection,
  ) {
    baseFormManager.registerFormStateChange((prevValue) => ({
      ...prevValue,
      hotWork:
        hotWorkSection === HotWorkSection.HotWork
          ? hotWorkTransformation(prevValue.hotWork)
          : prevValue.hotWork,
      hotWorkCategoryOneConditions:
        hotWorkSection === HotWorkSection.HotWorkCategoryOne
          ? hotWorkTransformation(prevValue.hotWorkCategoryOneConditions)
          : prevValue.hotWorkCategoryOneConditions,
      hotWorkCategoryTwoConditions:
        hotWorkSection === HotWorkSection.HotWorkCategoryTwo
          ? hotWorkTransformation(prevValue.hotWorkCategoryTwoConditions)
          : prevValue.hotWorkCategoryTwoConditions,
      hotWorkCategoryThreeConditions:
        hotWorkSection === HotWorkSection.HotWorkCategoryThree
          ? hotWorkTransformation(prevValue.hotWorkCategoryThreeConditions)
          : prevValue.hotWorkCategoryThreeConditions,
    }));
  }

  function protectiveEquipmentChangeHandler(
    protectiveEquipmentTransformation: Transformation<ProtectiveEquipmentFormState>,
  ) {
    baseFormManager.registerFormStateChange((prevValue) => ({
      ...prevValue,
      protectiveEquipment: protectiveEquipmentTransformation(
        prevValue.protectiveEquipment,
      ),
    }));
  }

  function measuresChangeHandler(
    measuresTransformation: Transformation<MeasuresFormState>,
  ) {
    baseFormManager.registerFormStateChange((prevValue) => ({
      ...prevValue,
      measures: measuresTransformation(prevValue.measures),
    }));
  }

  function riskIdentificationsChangeHandler(
    riskIdentificationsTransformation: Transformation<RiskIdentificationsFormState>,
  ) {
    baseFormManager.registerFormStateChange((prevValue) => ({
      ...prevValue,
      riskIdentifications: riskIdentificationsTransformation(
        prevValue.riskIdentifications,
      ),
    }));
  }

  function permitAgreementChangeHandler(
    transformation: Transformation<PermitAgreementFormState>,
  ) {
    baseFormManager.registerFormStateChange((prevValue) => ({
      ...prevValue,
      permitAgreement: transformation(prevValue.permitAgreement),
    }));
  }

  async function submitUpdateHotWorkHandlerAsync(
    e: React.FormEvent<HTMLFormElement>,
  ) {
    e.preventDefault();

    if (!storedPermit) {
      console.error("Tried to update hot work with no stored permit in state.");
      return;
    }

    const hotWorkSignaturesWillBeCleared = hasHotWorkSignatureClearingChanges(
      storedPermit,
      baseFormManager.formState,
    );

    if (hotWorkSignaturesWillBeCleared) {
      await showDialogAsync({
        cancelButtonText: props.resources.get("No") ?? "",
        confirmButtonText: props.resources.get("YesSaveChanges") ?? "",
        html:
          props.resources.get("UpdateClearsHotWorkSignaturesDescription") ?? "",
        title: props.resources.get("UpdateClearsHotWorkSignaturesTitle") ?? "",
        type: "warning",
        onConfirmAsync: updateHotWorkHandlerAsync,
      });

      return;
    }

    await updateHotWorkHandlerAsync();
  }

  async function updateHotWorkHandlerAsync() {
    if (!storedPermit) {
      console.error(
        "updateHotWorkHandlerAsync was called with no storedPermit in state.",
      );
      return;
    }

    const request = EnergyHarbourPermitFormState_toRequest(
      baseFormManager.formState,
    );

    baseFormManager.setRequestInProgress(true);
    const updateResult = await updateEnergyHarbourHotWorkAsync(
      storedPermit.id,
      request,
    );
    baseFormManager.setRequestInProgress(false);

    if (updateResult.failed) {
      console.error(updateResult.error);
      const errorLocations = baseFormManager.getErrorLocations(
        updateResult.error,
        props.resources,
      );
      showValidationErrorDialog(errorLocations, props.resources);
      return;
    }

    showSuccessAlert(props.resources.get("HotWorkConditionsUpdated"));
    baseFormManager.setHasUnsavedChanges(false);

    await baseFormManager.refreshStoredPermitAsync();
  }

  async function saveIsFinishedPermitProviderSignatureManagerAsync(
    signatureBlob: Blob,
    nameClarification: string,
  ) {
    if (!storedPermit) {
      console.error("Tried to sign with no stored permit in state.");
      return;
    }

    await baseFormManager.createSignatureManagerAsync(
      signatureBlob,
      nameClarification,
      SignatureType.IsFinishedPermitProvider,
    );

    if (storedPermit.isFinishedPermitSeekerSignature.id) {
      await finishPermitEnergyHarbourPermitAsync();
    }
  }

  async function saveIsFinishedPermitSeekerSignatureManagerAsync(
    signatureBlob: Blob,
    nameClarification: string,
  ) {
    if (!storedPermit) {
      console.error("Tried to sign with no stored permit in state.");
      return;
    }

    await baseFormManager.createSignatureManagerAsync(
      signatureBlob,
      nameClarification,
      SignatureType.IsFinishedPermitSeeker,
    );

    if (storedPermit.isFinishedPermitProviderSignature.id) {
      await finishPermitEnergyHarbourPermitAsync();
    }
  }

  async function finishPermitEnergyHarbourPermitAsync() {
    if (!storedPermit) {
      console.error("Tried to finish permit with no stored permit in state.");
      return;
    }

    const finishPermitResult = await finishPermitAsync(
      storedPermit.entityId,
      PermitType.EnergyHarbourPermit,
    );

    if (finishPermitResult.failed) {
      console.error(finishPermitResult.error);
      showDangerDialog(
        props.resources.get("SomethingWentWrong"),
        props.resources.get("FinishError"),
      );
      return;
    }

    showSuccessAlert(props.resources.get("PermitFinished"));
    await baseFormManager.refreshStoredPermitAsync();
  }

  async function checkoutPermitClickHandlerAsync() {
    if (!storedPermit) {
      console.error("Tried to check out with no stored permit in state.");
      return;
    }

    const checkoutResult = await checkoutPermitAsync(
      storedPermit.entityId,
      PermitType.EnergyHarbourPermit,
    );
    if (checkoutResult.failed) {
      console.error(checkoutResult.error);
      showDangerDialog(
        props.resources.get("SomethingWentWrong"),
        props.resources.get("CheckoutError"),
      );
      return;
    }

    const lastWorkPermitVersionId = checkoutResult.value;
    if (lastWorkPermitVersionId !== storedPermit.entityId) {
      window.location.href = `/Permits/EnergyHarbourPermit?workPermitId=${lastWorkPermitVersionId}`;
      return;
    }

    showSuccessAlert(props.resources.get("PermitCheckedOut"));
    await baseFormManager.refreshStoredPermitAsync();
  }

  async function updatePositionsClickHandlerAsync() {
    if (!storedPermit) {
      console.error(
        "Tried to update positions with no stored permit in state.",
      );
      return;
    }

    const updateResult = await updatePositionsAsync(
      storedPermit.entityId,
      baseFormManager.formState.positions.map(PositionPickerRow_toPosition),
      PermitType.EnergyHarbourPermit,
    );

    if (updateResult.failed) {
      console.error(updateResult);
      showDangerDialog(
        props.resources.get("SomethingWentWrong"),
        props.resources.get("RefreshPageAndTryAgain"),
      );
      return;
    }

    showSuccessAlert(props.resources.get("PositionsUpdated"));
    baseFormManager.setFormState((prevValue) => ({
      ...prevValue,
      positions: prevValue.positions.map(PositionPickerRow_clearErrors),
    }));
  }

  async function updatePermitAgreementEndDateClickHandlerAsync() {
    if (!storedPermit) {
      console.error(
        "Tried to update permit agreement end date with no stored permit.",
      );
      return;
    }

    const startDate = baseFormManager.formState.permitAgreement.startDate.value;
    const updatedEndDate =
      baseFormManager.formState.permitAgreement.endDate.value;

    const newEndDateBeforeStart = updatedEndDate < startDate;
    if (newEndDateBeforeStart) {
      baseFormManager.setFormState((prevValue) => ({
        ...prevValue,
        permitAgreement: {
          ...prevValue.permitAgreement,
          endDate: {
            ...prevValue.permitAgreement.endDate,
            errorMessage: props.resources.get("EndDateStartError"),
          },
        },
      }));

      return;
    }

    const newDuration = Math.ceil(Date_hoursBetween(startDate, updatedEndDate));
    const newPermitDurationExceedsMaxDuration = newDuration > 24;
    if (newPermitDurationExceedsMaxDuration) {
      baseFormManager.setFormState((prevValue) => ({
        ...prevValue,
        permitAgreement: {
          ...prevValue.permitAgreement,
          endDate: {
            ...prevValue.permitAgreement.endDate,
            errorMessage: props.resources.get(
              "PermitVersionExceedsAllowedDuration",
            ),
          },
        },
      }));

      return;
    }

    baseFormManager.setFormState((prevValue) => ({
      ...prevValue,
      permitAgreement: {
        ...prevValue.permitAgreement,
        endDate: {
          ...prevValue.permitAgreement.endDate,
          errorMessage: "",
        },
      },
    }));

    const updateResult = await updatePermitAgreementEndDateAsync(
      storedPermit.entityId,
      baseFormManager.formState.permitAgreement.endDate.value,
    );

    if (updateResult.failed) {
      console.error(updateResult.error);

      if (!updateResult.error.errors) {
        console.error(updateResult.error);
        showDangerDialog(
          props.resources.get("SomethingWentWrong"),
          props.resources.get("UpdatePermitAgreementEndDateError"),
        );
        return;
      }

      baseFormManager.setFormState((prevValue) => {
        try {
          const updatedFormState =
            EnergyHarbourPermitFormState_populateErrorMessages(
              prevValue,
              updateResult.error.errors,
            );
          return updatedFormState;
        } catch (error) {
          console.error("Failed to populate error messages", error);
          return prevValue;
        }
      });

      const errorLocations = baseFormManager.getErrorLocations(
        updateResult.error,
        props.resources,
      );
      showValidationErrorDialog(errorLocations, props.resources);

      return;
    }

    showSuccessAlert(props.resources.get("PermitAgreementEndDateUpdated"));
  }

  async function updateParticipantsClickHandlerAsync() {
    if (!storedPermit) {
      console.error(
        "Tried to update participants with no stored permit in state.",
      );
      return;
    }

    const request: UpdateEnergyHarbourPermitParticipantsRequest = {
      participants: baseFormManager.formState.persons.map(
        PersonFormElement_toPerson,
      ),
      projectLeader: PersonFormElement_toPerson(
        baseFormManager.formState.projectLeader,
      ),
      workLeader: PersonFormElement_toPerson(
        baseFormManager.formState.workLeader,
      ),
    };

    const updateResult = await updateEnergyHarbourPermitParticipantsAsync(
      storedPermit.entityId,
      request,
    );

    if (updateResult.failed) {
      console.error(updateResult.error);
      showDangerDialog(
        props.resources.get("SomethingWentWrong"),
        props.resources.get("UpdateParticipantsError"),
      );
      return;
    }

    await baseFormManager.refreshStoredPermitAsync();

    showSuccessAlert(props.resources.get("ParticipantsUpdated"));
  }

  async function updatePermitHandlerAsync() {
    if (!storedPermit) {
      console.error("Tried to update hot work with no stored permit in state.");
      return;
    }

    const hotWorkSignaturesWillBeCleared = hasHotWorkSignatureClearingChanges(
      storedPermit,
      baseFormManager.formState,
    );

    if (hotWorkSignaturesWillBeCleared) {
      await showDialogAsync({
        cancelButtonText: props.resources.get("No") ?? "",
        confirmButtonText: props.resources.get("YesSaveChanges") ?? "",
        html:
          props.resources.get("UpdateClearsHotWorkSignaturesDescription") ?? "",
        title: props.resources.get("UpdateClearsHotWorkSignaturesTitle") ?? "",
        type: "warning",
        onConfirmAsync: () =>
          baseFormManager.updatePermitHandlerAsync({
            clearSignaturesConfirmed: true,
          }),
      });

      return;
    }

    baseFormManager.updatePermitHandlerAsync();
  }

  function handleOrderedByCustomerSelected(
    _: EnergyHarbourCustomer[],
    selectedItem: EnergyHarbourCustomer,
  ) {
    baseFormManager.registerFormStateChange((prevValue) => ({
      ...prevValue,
      orderedByCustomer: {
        ...prevValue.orderedByCustomer,
        value: {
          companyId: selectedItem.companyId,
          companyDepartmentId: selectedItem.companyDepartmentId,
        },
      },
    }));
  }

  function handleOrderedByCustomerRemoved() {
    baseFormManager.registerFormStateChange((prevValue) => ({
      ...prevValue,
      orderedByCustomer: {
        ...prevValue.orderedByCustomer,
        value: undefined,
      },
    }));
  }

  const isNewPermit = !storedPermit;

  const canManagePositionActiveStatus =
    isNewPermit || storedPermit.canUpdatePositionActiveStatus;
  const canManageStartDate = isNewPermit || storedPermit.canUpdateStartDate;
  const canManageEndDate = isNewPermit || storedPermit.canUpdateEndDate;
  const canManageOrderedBy = isNewPermit || storedPermit.canManageOrderedBy;
  const canManageParticipants =
    isNewPermit || storedPermit.canUpdateParticipants;
  const canManageHotWorkConditions =
    isNewPermit || storedPermit.canManageHotWorkConditions;
  const canManageDescription = isNewPermit || storedPermit.canUpdateDescription;
  const canManagePositions = isNewPermit || storedPermit.canUpdatePositions;
  const canManagePermitCategory =
    isNewPermit || storedPermit.canManagePermitCategory;
  const canManageColdWorkCategory =
    canManagePermitCategory &&
    storedPermit &&
    !storedPermit.isColdWorkCategoryRequired;
  const canManagePositionCategories =
    isNewPermit || storedPermit.canManagePositionCategories;

  const canAccept =
    storedPermit &&
    storedPermit.status === CodeStatus.Registered &&
    Date_isToday(storedPermit.permitAgreement.startDate) &&
    !Date_hasPassed(storedPermit.permitAgreement.startDate) &&
    !!storedPermit.permitAgreement.permitAgreementProviderSignature.id &&
    !!storedPermit.permitAgreement.permitAgreementSeekerSignature.id;

  const showStartTimeHasPassed =
    storedPermit &&
    storedPermit.status === CodeStatus.Registered &&
    Date_hasPassed(storedPermit.permitAgreement.startDate) &&
    !!storedPermit.permitAgreement.permitAgreementProviderSignature.id &&
    !!storedPermit.permitAgreement.permitAgreementSeekerSignature.id;

  const showStartDateInFuture =
    storedPermit &&
    storedPermit.status === CodeStatus.Registered &&
    !Date_isToday(storedPermit.permitAgreement.startDate) &&
    !Date_hasPassed(storedPermit.permitAgreement.startDate) &&
    !!storedPermit.permitAgreement.permitAgreementProviderSignature.id &&
    !!storedPermit.permitAgreement.permitAgreementSeekerSignature.id;

  const showAgreementSection = !!storedPermit;

  const showFinishPermitLink =
    !!storedPermit &&
    storedPermit.canViewFinishPermitLink &&
    !!storedPermit.lastPermitInSeriesDocumentId;

  const showUpdatePositionsButton =
    !!storedPermit &&
    !storedPermit.canUpdate &&
    storedPermit.canUpdatePositionActiveStatus;

  const showUpdateParticipantsButton =
    !!storedPermit &&
    storedPermit.status === CodeStatus.Accepted &&
    storedPermit.canUpdateParticipants;

  const showFinishPermitAccordion =
    !!storedPermit && storedPermit.status !== CodeStatus.Registered;

  const showHotWorkConditions =
    !!storedPermit &&
    !storedPermit.supportsCategorySpecificHotWorkConditions &&
    (baseFormManager.formState.permitCategories.value.includes(
      PermitCategory.HotWorkCategoryOne,
    ) ||
      baseFormManager.formState.permitCategories.value.includes(
        PermitCategory.HotWorkCategoryTwo,
      ) ||
      baseFormManager.formState.permitCategories.value.includes(
        PermitCategory.HotWorkCategoryThree,
      ));

  const showHotWorkCatOneConditions =
    (!storedPermit || storedPermit.supportsCategorySpecificHotWorkConditions) &&
    baseFormManager.formState.permitCategories.value.includes(
      PermitCategory.HotWorkCategoryOne,
    );

  const showHotWorkCatTwoConditions =
    (!storedPermit || storedPermit.supportsCategorySpecificHotWorkConditions) &&
    baseFormManager.formState.permitCategories.value.includes(
      PermitCategory.HotWorkCategoryTwo,
    );

  const showHotWorkCatThreeConditions =
    (!storedPermit || storedPermit.supportsCategorySpecificHotWorkConditions) &&
    baseFormManager.formState.permitCategories.value.includes(
      PermitCategory.HotWorkCategoryThree,
    );

  const showUpdateHotWorkConditionsButton =
    !!storedPermit && storedPermit.canManageHotWorkConditions;

  const canUpdatePermitAgreementEndDate =
    !!storedPermit && storedPermit.canUpdatePermitAgreementEndDate;
  const showUpdatePermitAgreementEndDateButton =
    !baseFormManager.canUpdate && canUpdatePermitAgreementEndDate;

  const showMissingHotWorkSignatureAlert =
    !!storedPermit && storedPermit.showMissingHotWorkSignatureAlert;

  const showUnderWaterWorkPermitIdMissing =
    formState.riskIdentifications.isRaftIncludedInPrint.value &&
    !formState.riskIdentifications.raftChecklist
      .underWaterWorkPermitSequenceIdentifier.value;

  const showDivingPermitIdMissing =
    formState.riskIdentifications.isDivingIncludedInPrint.value &&
    !formState.riskIdentifications.divingChecklist
      .divingPermitSequenceIdentifier.value;

  const participantCount =
    Array_sum(
      baseFormManager.formState.persons.map((p) => (p.isActive ? 1 : 0)),
    ) +
    (baseFormManager.formState.workLeader.isActive ? 1 : 0) +
    (baseFormManager.formState.projectLeader.isActive ? 1 : 0);

  const startDateMin = !storedPermit
    ? new Date()
    : !canManageStartDate
      ? null
      : //
        // Permits without a hot work category cannot be updated if the specified start date is in the past.
        //
        formState.permitCategories.value.includes(PermitCategory.ColdWork) &&
          formState.permitCategories.value.length === 1
        ? new Date()
        : //
          // Permits that include a hot work category can be updated if the specified start date is in the past, as long as the start date is not moved
          // further back than the date stored in the database.
          //
          Date_min(storedPermit.startDate, new Date());

  return {
    showStartTimeHasPassed,
    showStartDateInFuture,
    showUpdatePositionsButton,
    showHotWorkCategoryOneOptionsModal,
    showHotWorkCategoryTwoOptionsModal,
    showHotWorkCategoryThreeOptionsModal,
    showUpdateParticipantsButton,
    showHotWorkConditions,
    showHotWorkCatOneConditions,
    showHotWorkCatTwoConditions,
    showHotWorkCatThreeConditions,
    showFinishPermitLink,
    showFinishPermitAccordion,
    showUpdatePermitAgreementEndDateButton,
    showMissingHotWorkSignatureAlert,
    showUnderWaterWorkPermitIdMissing,
    showDivingPermitIdMissing,

    canAccept,
    canManagePositionActiveStatus,
    canManageOrderedBy,
    showUpdateHotWorkConditionsButton,
    canUpdatePermitAgreementEndDate,
    canManageHotWorkConditions,
    canManageDescription,
    canManageStartDate,
    canManageEndDate,
    canManagePositions,
    canManagePermitCategory,
    canManagePositionCategories,
    canManageColdWorkCategory,

    powerOffResponsibleElectricianSignatureManager,
    powerOffWorkLeaderSignatureManager,
    powerOffDirectorFlammableProductSignatureManager,
    powerOffExternalDirectorFlammableProductSignatureManager,
    powerOnResponsibleElectricianSignatureManager,
    powerOnWorkLeaderSignatureManager,
    powerOnDirectorFlammableProductSignatureManager,
    powerOnExternalDirectorFlammableProductSignatureManager,
    powerOnPortOfficerSignatureManager,
    powerOffPortOfficerSignatureManager,
    craneOperatorSignatureManager,
    safetyAgreementPermitSeekerSignatureManager,
    permitAgreementPermitSeekerSignatureManager,
    permitAgreementPermitProviderSignatureManager,
    hotWorkInternalDirectorFlammableProductSignatureManager,
    hotWorkExternalDirectorFlammableProductSignatureManager,
    isFinishedPermitProviderSignatureManager,
    isFinishedPermitSeekerSignatureManager,
    safeHarbourEducationControlledSignatureManager,
    hotWorkCounterSignatureRequestHandler,
    electricFencePowerOffCounterSignatureRequestHandler,
    electricFencePowerOnCounterSignatureRequestHandler,

    riskIdentificationsUIState,
    measuresUIState,

    storedPermit,
    permitAgreementStartDateRef,
    permitAgreementEndDateRef,
    canManageParticipants,
    showAgreementSection,
    participantCount,
    startDateHandler,
    endDateHandler,
    permitAgreementStartDateHandler,
    permitAgreementEndDateHandler,
    startDateMin,
    energyHarbourCustomers,

    department: baseFormManager.department,
    formState: baseFormManager.formState,
    isLoading: baseFormManager.isLoading,
    isNewPermit: baseFormManager.isNewPermit,
    canCreate: baseFormManager.canCreate,
    canUpdate: baseFormManager.canUpdate,
    showAcceptUnsavedChangesModal:
      baseFormManager.showAcceptUnsavedChangesModal,
    requestInProgress: baseFormManager.requestInProgress,
    hasUnsavedChanges: baseFormManager.hasUnsavedChanges,
    canManageContent: baseFormManager.canManageContent,
    canManagePositionPolygons: baseFormManager.canManagePositionPolygons,
    canCreateUnderWaterWorkPermits: props.canCreateUnderWaterWorkPermits,
    canCreateDivingPermits: props.canCreateDivingPermits,

    permitCategoryChangeHandler,
    permitCategoryAddedHandler,
    permitCategoryRemovedHandler,
    hotWorkCategoryOneOptionsChangeHandler,
    hotWorkCategoryTwoOptionsChangeHandler,
    hotWorkCategoryThreeOptionsChangeHandler,
    hotWorkChangeHandler,
    protectiveEquipmentChangeHandler,
    riskIdentificationsChangeHandler,
    measuresChangeHandler,
    setShowHotWorkCategoryOneOptionsModal,
    setShowHotWorkCategoryTwoOptionsModal,
    setShowHotWorkCategoryThreeOptionsModal,
    permitAgreementChangeHandler,
    submitUpdateHotWorkHandlerAsync,
    checkoutPermitClickHandlerAsync,
    updatePositionsClickHandlerAsync,
    updatePermitAgreementEndDateClickHandlerAsync,
    updateParticipantsClickHandlerAsync,
    handleOrderedByCustomerSelected,
    handleOrderedByCustomerRemoved,

    createSignatureManagerAsync: baseFormManager.createSignatureManagerAsync,
    removeSignatureManagerAsync: baseFormManager.removeSignatureManagerAsync,
    addPositionPickerRowHandler: baseFormManager.addPositionPickerRowHandler,
    updatePositionPickerRowHandler:
      baseFormManager.updatePositionPickerRowHandler,
    removePositionPickerRowHandler:
      baseFormManager.removePositionPickerRowHandler,
    formStateChangeHandler: baseFormManager.formStateChangeHandler,
    addParticipantHandler: baseFormManager.addParticipantHandler,
    removeParticipantHandler: baseFormManager.removeParticipantHandler,
    personFormElementChangeHandler:
      baseFormManager.personFormElementChangeHandler,
    submitCreateHandlerAsync: baseFormManager.submitCreateHandlerAsync,
    updatePermitHandlerAsync: baseFormManager.updatePermitHandlerAsync,
    formKeyDownHandler: baseFormManager.formKeyDownHandler,
    setShowAcceptUnsavedChangesModal:
      baseFormManager.setShowAcceptUnsavedChangesModal,
    submitUpdateHandlerAsync: baseFormManager.submitUpdateHandlerAsync,
    unsavedChangesModalUpdateHandlerAsync:
      baseFormManager.unsavedChangesModalUpdateHandlerAsync,
    unsavedChangesModalAcceptHandlerAsync:
      baseFormManager.unsavedChangesModalAcceptHandlerAsync,
    copyButtonClickHandler: baseFormManager.copyButtonClickHandler,
    acceptButtonClickHandler: baseFormManager.acceptButtonClickHandler,
    updateParticipantHandler: baseFormManager.updateParticipantHandler,
    registerFormStateChange: baseFormManager.registerFormStateChange,
    renewPermitClickHandlerAsync: baseFormManager.renewPermitClickHandlerAsync,
  };
}

export default useEnergyHarbourPermitForm;
